import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { X, Lock, ArrowUpRight, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeckAccessModal({ open, onClose, deck }) {
  const deckKey = deck?.key || "master";
  const deckTitle = deck?.title || "Master Deck";
  const deckFilename = deck?.filename || "DCYB-Master-Pitch-Deck.pdf";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");
  const [deckUrl, setDeckUrl] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    if (open && user) {
      setName((v) => v || user.name || "");
      setEmail((v) => v || user.email || "");
    }
  }, [open, user]);

  useEffect(() => {
    if (!open) {
      setStatus("idle");
      setError("");
      setPassword("");
      if (deckUrl) URL.revokeObjectURL(deckUrl);
      setDeckUrl("");
    }
  }, [open, deckUrl]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${API}/deck/access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, company, password, deck: deckKey }),
      });

      if (res.status === 401) {
        setStatus("error");
        setError("Incorrect password. Try again.");
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setStatus("error");
        setError(data.detail || "Something went wrong. Please try again.");
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setDeckUrl(url);
      setStatus("success");
      // Open the deck in a new tab automatically
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setStatus("error");
      setError("Network error. Please check your connection and try again.");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="deck-access-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deck-modal-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close modal"
        onClick={onClose}
        className="absolute inset-0 bg-[#0F1117]/60 backdrop-blur-sm"
      />

      {/* Panel */}
      <div className="relative w-full max-w-md chrome-surface p-6 md:p-8">
        <button
          type="button"
          data-testid="deck-modal-close"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center text-[#5F6168] hover:text-[#0F1117] transition-colors"
        >
          <X size={16} />
        </button>

        {status !== "success" ? (
          <>
            <div className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#5F6168] mb-3 flex items-center gap-2">
              <Lock size={11} aria-hidden />
              Restricted · {deckTitle}
            </div>
            <h3
              id="deck-modal-title"
              className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-[#0F1117] leading-tight mb-2"
            >
              Request Access.
            </h3>
            <p className="text-[13px] text-[#5F6168] leading-relaxed mb-6">
              The {deckTitle.toLowerCase()} is shared under NDA with prospective
              partners and investors. Enter your details and access password
              below.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-[#71747B]">
                  Full Name
                </span>
                <input
                  type="text"
                  required
                  data-testid="deck-modal-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-white border border-[#D2D5DA] focus:border-[#0F1117] focus:outline-none px-3 py-2.5 text-[14px] text-[#0F1117] transition-colors"
                  placeholder="Jane Doe"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-[#71747B]">
                  Work Email
                </span>
                <input
                  type="email"
                  required
                  data-testid="deck-modal-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white border border-[#D2D5DA] focus:border-[#0F1117] focus:outline-none px-3 py-2.5 text-[14px] text-[#0F1117] transition-colors"
                  placeholder="jane@company.com"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-[#71747B]">
                  Company{" "}
                  <span className="text-[#B0B4BA] normal-case tracking-normal">
                    (optional)
                  </span>
                </span>
                <input
                  type="text"
                  data-testid="deck-modal-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="bg-white border border-[#D2D5DA] focus:border-[#0F1117] focus:outline-none px-3 py-2.5 text-[14px] text-[#0F1117] transition-colors"
                  placeholder="Company name"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-[#71747B]">
                  Access Password
                </span>
                <input
                  type="password"
                  required
                  data-testid="deck-modal-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-white border border-[#D2D5DA] focus:border-[#0F1117] focus:outline-none px-3 py-2.5 text-[14px] text-[#0F1117] transition-colors"
                  placeholder="Enter password"
                  autoComplete="off"
                />
              </label>

              {error && (
                <div
                  data-testid="deck-modal-error"
                  className="mt-1 text-[12px] text-[#B03A3A] font-mono-label tracking-wide"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="deck-modal-submit"
                className="chrome-btn-dark mt-3 inline-flex items-center justify-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-5 py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "loading" ? (
                  <>Verifying…</>
                ) : (
                  <>
                    Unlock The Deck
                    <ArrowUpRight size={14} />
                  </>
                )}
              </button>
              <p className="mt-1 font-mono-label text-[9.5px] tracking-[0.14em] uppercase text-[#9498A0] text-center">
                Access is logged · NDA implied
              </p>
            </form>
          </>
        ) : (
          <div data-testid="deck-modal-success" className="text-center py-2">
            <div className="mx-auto w-12 h-12 rounded-full bg-[#0F1117] text-white flex items-center justify-center mb-4">
              <ArrowUpRight size={20} />
            </div>
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-[#0F1117] mb-2">
              Access Granted.
            </h3>
            <p className="text-[13px] text-[#5F6168] leading-relaxed mb-6">
              The deck opened in a new tab. You can also download a copy for
              offline review below.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={deckUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="deck-modal-open-again"
                className="chrome-btn-dark inline-flex items-center justify-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-5 py-3 flex-1"
              >
                Open Deck
                <ArrowUpRight size={14} />
              </a>
              <a
                href={deckUrl}
                download={deckFilename}
                data-testid="deck-modal-download"
                className="chrome-btn inline-flex items-center justify-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-5 py-3 flex-1"
              >
                <Download size={14} />
                Download
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
